import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { TopbarLayout } from './TopbarLayout';
import { MinimalLayout } from './MinimalLayout';
import { DashboardFirstLayout } from './DashboardFirstLayout';
import { useThemeStore, SIDEBAR_COLLAPSED_WIDTH } from '../../stores/theme.store';
import { useTenantTheme } from '../../hooks/useTenantTheme';
import { ContextualHelp } from '../help';
import { OnboardingTour } from '../help/OnboardingTour';
import { ProactiveTip } from '../help/ProactiveTip';
import { SetupProgress } from '../help/SetupProgress';
import { useAuthStore } from '../../stores/auth.store';

/**
 * MainLayout - Componente que renderiza o layout correto baseado no tema do tenant
 * 
 * Layouts disponíveis:
 * - SIDEBAR: Menu lateral fixo à esquerda (padrão)
 * - TOPBAR: Menu horizontal no topo
 * - MINIMAL: Menu hamburger colapsável
 * - DASHBOARD_FIRST: Dashboard como página inicial com sidebar compacta
 * 
 * O layout é determinado pelo tema do tenant carregado via useTenantTheme.
 * Super Admins sempre usam SIDEBAR para manter consistência.
 */
export function MainLayout() {
  const { sidebarCollapsed, sidebarWidth, layoutType } = useThemeStore();
  
  // Carrega e aplica o tema do tenant automaticamente
  // Isso também define o layoutType no store
  const { isLoading: themeLoading, isSuperAdmin } = useTenantTheme();

  // Debug log para desenvolvimento
  console.log('[MainLayout] Current layout:', layoutType, '| Super Admin:', isSuperAdmin);

  // Renderiza o layout baseado no tipo
  // Super Admin sempre usa SIDEBAR
  const effectiveLayout = isSuperAdmin ? 'SIDEBAR' : layoutType;

  switch (effectiveLayout) {
    case 'TOPBAR':
      return <TopbarLayout />;
    
    case 'MINIMAL':
      return <MinimalLayout />;
    
    case 'DASHBOARD_FIRST':
      return <DashboardFirstLayout />;
    
    case 'SIDEBAR':
    default:
      return <SidebarLayout sidebarCollapsed={sidebarCollapsed} sidebarWidth={sidebarWidth} />;
  }
}

/**
 * SidebarLayout - Layout padrão com sidebar lateral redimensionável
 */
function SidebarLayout({ sidebarCollapsed, sidebarWidth }: { sidebarCollapsed: boolean; sidebarWidth: number }) {
  // Calculate actual padding based on collapsed state
  const actualPadding = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;

  return (
    <div className="min-h-screen bg-surface-950">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-30" />
      </div>

      <Sidebar />
      <Header />

      <main
        className="pt-16 min-h-screen transition-all duration-300"
        style={{ paddingLeft: `${actualPadding}px` }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-6"
        >
          <AdminSetupProgressWrapper />
          <Outlet />
        </motion.div>
      </main>

      {/* Contextual Help Button */}
      <ContextualHelp />

      {/* Onboarding Tour - shows on first access based on user profile */}
      <OnboardingTourWrapper />

      {/* Proactive Tips - shows contextual tips on first visit to sections */}
      <ProactiveTip />
    </div>
  );
}

/**
 * OnboardingTourWrapper - Determines user profile and renders OnboardingTour with auto-start
 */
function OnboardingTourWrapper() {
  const { user } = useAuthStore();

  if (!user) return null;

  const profileType: 'ADMIN' | 'CUSTOMER' | 'COMPLIANCE' | 'OPERATOR' =
    user.type === 'ADMIN' ? 'ADMIN'
    : user.type === 'COMPLIANCE' ? 'COMPLIANCE'
    : user.type === 'OPERATOR' ? 'OPERATOR'
    : 'CUSTOMER';

  return <OnboardingTour profileType={profileType} autoStart />;
}

/**
 * AdminSetupProgressWrapper - Shows setup progress only for ADMIN on the admin dashboard
 */
function AdminSetupProgressWrapper() {
  const { user } = useAuthStore();
  const location = useLocation();

  // Only show on admin dashboard
  if (user?.type !== 'ADMIN' || location.pathname !== '/admin') return null;

  return (
    <div className="mb-6">
      <SetupProgress />
    </div>
  );
}
